import {
    keepPreviousData,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
    type QueryClient
} from '@tanstack/react-query';
import {
    createPurchaseOrder,
    fetchBillingVendors,
    fetchPurchaseOrder,
    fetchPurchaseOrders,
    recordVendorInvoice,
    updatePurchaseOrderLine,
    type CreatePurchaseOrderBody,
    type PurchaseOrderDetail,
    type PurchaseOrderStageSummary,
    type PurchaseOrderVendorRef,
    type PurchaseOrdersListParams,
    type RecordVendorInvoiceBody,
    type UpdatePurchaseOrderLineBody
} from '../services/purchaseOrders';
import {
    BILLING_CHARGES_KEY,
    BILLING_VENDORS_KEY,
    BILLING_PURCHASE_ORDER_KEY,
    BILLING_PURCHASE_ORDERS_INFINITE_KEY,
    BILLING_PURCHASE_ORDERS_KEY
} from './queryKeys';

const STALE_TIME_MS = 60 * 1000;

// Stable identity: the fallback feeds a render-time option list.
const EMPTY_VENDORS: PurchaseOrderVendorRef[] = [];

// Stable identity so an idle/loading render doesn't hand the status cards a
// fresh object every time and thrash their memoization.
const EMPTY_STAGE_SUMMARY: PurchaseOrderStageSummary = {
    all: { count: 0 },
    awaitingShipment: { count: 0 },
    awaitingInvoice: { count: 0 },
    needsAttention: { count: 0 },
    complete: { count: 0 }
};

function listKeyParts(params: Omit<PurchaseOrdersListParams, 'page'>) {
    return [
        params.limit,
        params.q ?? '',
        params.chargeId ?? '',
        params.practiceId ?? '',
        params.vendorId?.join(',') ?? '',
        params.fulfillmentStatus?.join(',') ?? '',
        params.billingStatus?.join(',') ?? '',
        params.needsAttention ? '1' : '',
        params.sort ?? '',
        params.order ?? ''
    ];
}

export function usePurchaseOrders(params: PurchaseOrdersListParams & { enabled?: boolean }) {
    const { enabled = true, ...rest } = params;

    const { data, isLoading, isFetching, isError } = useQuery({
        queryKey: [BILLING_PURCHASE_ORDERS_KEY, 'paged', rest.page, ...listKeyParts(rest)],
        queryFn: () => fetchPurchaseOrders(rest),
        staleTime: STALE_TIME_MS,
        placeholderData: keepPreviousData,
        enabled
    });

    return {
        purchaseOrders: data?.data ?? [],
        total: data?.total ?? 0,
        stageSummary: data?.stageSummary ?? EMPTY_STAGE_SUMMARY,
        isLoading,
        isFetching,
        isError
    };
}

// Same endpoint accumulated instead of sliced - DataTable's 'infinite' mode.
// The key omits `page` so a filter/search change starts a fresh page-1 fetch.
export function useInfinitePurchaseOrders(
    params: Omit<PurchaseOrdersListParams, 'page'> & { enabled?: boolean }
) {
    const { enabled = true, ...rest } = params;
    const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
        useInfiniteQuery({
            queryKey: [...BILLING_PURCHASE_ORDERS_INFINITE_KEY, ...listKeyParts(rest)],
            initialPageParam: 1,
            queryFn: ({ pageParam }) => fetchPurchaseOrders({ ...rest, page: pageParam }),
            // The list response carries `total` only, so the next page is
            // derived from the page just asked for rather than an echoed one.
            getNextPageParam: (lastPage, _pages, lastPageParam) =>
                lastPageParam * rest.limit < lastPage.total ? lastPageParam + 1 : undefined,
            staleTime: STALE_TIME_MS,
            placeholderData: keepPreviousData,
            enabled
        });

    return {
        purchaseOrders: data?.pages.flatMap((page) => page.data) ?? [],
        total: data?.pages[0]?.total ?? 0,
        stageSummary: data?.pages[0]?.stageSummary ?? EMPTY_STAGE_SUMMARY,
        isLoading,
        isFetching,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage
    };
}

// A short server-owned list that changes rarely - cached for the session.
const VENDORS_STALE_TIME_MS = 30 * 60 * 1000;

export function useBillingVendors(enabled = true) {
    const { data, isLoading } = useQuery({
        queryKey: BILLING_VENDORS_KEY,
        queryFn: fetchBillingVendors,
        staleTime: VENDORS_STALE_TIME_MS,
        enabled
    });

    return { vendors: data ?? EMPTY_VENDORS, isLoading: enabled && isLoading };
}

export function usePurchaseOrder(id: string) {
    const { data, isLoading, isError, error } = useQuery({
        queryKey: BILLING_PURCHASE_ORDER_KEY(id),
        queryFn: () => fetchPurchaseOrder(id),
        staleTime: STALE_TIME_MS,
        enabled: Boolean(id)
    });

    return { purchaseOrder: data, isLoading, isError, error };
}

// Every PO mutation returns the updated detail, so the drawer is written from
// the response and only the lists and dependent aggregates are refetched.
function applyUpdatedDetail(queryClient: QueryClient, updated: PurchaseOrderDetail): void {
    queryClient.setQueryData(BILLING_PURCHASE_ORDER_KEY(updated.id), updated);
    // List keys only - a bare prefix would match 'detail' too and immediately
    // refetch the response just written above.
    void queryClient.invalidateQueries({ queryKey: [BILLING_PURCHASE_ORDERS_KEY, 'paged'] });
    void queryClient.invalidateQueries({ queryKey: BILLING_PURCHASE_ORDERS_INFINITE_KEY });
    // A PO reaches charges through its lines' charge line items as well as its
    // originating charge, so every cached charge is invalidated, not just one.
    void queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
    void queryClient.invalidateQueries({ queryKey: ['v2/billing/overview'] });
}

function usePurchaseOrderMutation<TVariables>(
    mutationFn: (variables: TVariables) => Promise<PurchaseOrderDetail>
) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn,
        onSuccess: (updated) => applyUpdatedDetail(queryClient, updated)
    });
}

export function useUpdatePurchaseOrderLine() {
    return usePurchaseOrderMutation(
        ({ id, lineId, body }: { id: string; lineId: string; body: UpdatePurchaseOrderLineBody }) =>
            updatePurchaseOrderLine(id, lineId, body)
    );
}

export function useRecordVendorInvoice() {
    return usePurchaseOrderMutation(({ id, body }: { id: string; body: RecordVendorInvoiceBody }) =>
        recordVendorInvoice(id, body)
    );
}

export function useCreatePurchaseOrder() {
    return usePurchaseOrderMutation((body: CreatePurchaseOrderBody) => createPurchaseOrder(body));
}
